import * as searchIcon from '@nordhealth/icons/lib/assets/navigation-search.js'
import { html, LitElement, nothing } from 'lit'
import { customElement, property, query } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { ref } from 'lit/directives/ref.js'

import { styleMap } from 'lit/directives/style-map.js'
import { AriaDescriptionController } from '../common/controllers/AriaDescriptionController.js'
import { DirectionController } from '../common/controllers/DirectionController.js'
import { ResizeController } from '../common/controllers/ResizeController.js'
import { cond } from '../common/directives/cond.js'
import { NordEvent } from '../common/events.js'
import { getSubmitButton } from '../common/form.js'
import { cleanValue } from '../common/input.js'
import { AutocompleteMixin } from '../common/mixins/AutocompleteMixin.js'

import { FocusableMixin } from '../common/mixins/FocusableMixin.js'
import { FormAssociatedMixin } from '../common/mixins/FormAssociatedMixin.js'
import { InputMixin } from '../common/mixins/InputMixin.js'
import { ReadonlyMixin } from '../common/mixins/ReadonlyMixin.js'
import { SizeMixin } from '../common/mixins/SizeMixin.js'
import { TextSelectableMixin } from '../common/mixins/TextSelectableMixin.js'
import componentStyle from '../common/styles/Component.css'
import formFieldStyle from '../common/styles/FormField.css'
import textFieldStyle from '../common/styles/TextField.css'
import Icon from '../icon/Icon.js'
import { requestInputGroupContext } from '../input-group/_InputGroupContext.js'
import { InputGroupControlController } from '../input-group/controllers/InputGroupControlController.js'
import inputGroupControlStyle from '../input-group/InputGroupControl.css'
import style from './Input.css'

Icon.registerIcon(searchIcon)

const px = (value?: number) => (value ? `${value}px` : undefined)

type InputMode = 'none' | 'text' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search'
const defaultInputmodeMap: Record<string, InputMode> = {
  number: 'numeric',
  email: 'email',
  tel: 'tel',
  url: 'url',
  search: 'search',
}

/**
 * Inputs are used to allow users to provide text input when the expected input is short.
 * As well as plain text, Input supports various types of text, including passwords and numbers.
 *
 * @status ready
 * @category form
 * @slot label - Use when a label requires more than plain text.
 * @slot hint - Optional slot that holds hint text for the input.
 * @slot error - Optional slot that holds error text for the input.
 * @slot start - Optional slot used to place an icon or prefix at the start of the input.
 * @slot end - Optional slot used to place an icon or suffix at the end of the input.
 *
 * @cssprop [--n-input-inline-size=240px] - Controls the inline size, or width, of the input.
 * @cssprop [--n-input-block-size=var(--n-space-xl)] - Controls the block size, or height, of the input using our [spacing tokens](/tokens/#space).
 * @cssprop [--n-input-background=var(--n-color-surface)] - Controls the background of the input, using our [color tokens](/tokens/#color).
 * @cssprop [--n-input-color=var(--n-color-text)] - Controls the text color of the input, using our [color tokens](/tokens/#color).
 * @cssprop [--n-input-border-color=var(--n-color-border-strong)] - Controls the border color of the input, using our [color tokens](/tokens/#color).
 * @cssprop [--n-input-border-radius=var(--n-border-radius-s)] - Controls how rounded the corners are, using [border radius tokens](/tokens/#border-radius).
 * @cssprop [--n-input-text-align=start] - Controls the alignment of text within the input itself.
 * @cssprop [--n-label-color=var(--n-color-text)] - Controls the text color of the label, using our [color tokens](/tokens/#color).
 */
@customElement('nord-input')
export default class Input extends SizeMixin(
  FormAssociatedMixin(AutocompleteMixin(ReadonlyMixin(TextSelectableMixin(InputMixin(FocusableMixin(LitElement)))))),
) {
  static styles = [componentStyle, formFieldStyle, textFieldStyle, style, inputGroupControlStyle]

  @query('slot[name=\'start\']') private startSlot!: HTMLSlotElement
  @query('slot[name=\'end\']') private endSlot!: HTMLSlotElement
  private startObserver = new ResizeController(this, () => this.startSlot)
  private endObserver = new ResizeController(this, () => this.endSlot)
  private direction = new DirectionController(this)
  private inputGroupControl = new InputGroupControlController(this, () => this.focusableRef.value)
  private ariaDescriptionController = new AriaDescriptionController(
    this,
    () => this.focusableRef.value,
    () => this.controlAriaDescribedByElements,
  )

  /**
   * The type of the input.
   */
  @property({ reflect: true }) type:
    | 'text'
    | 'email'
    | 'password'
    | 'tel'
    | 'url'
    | 'search'
    | 'number'
    | 'date'
    | 'time'
    | 'datetime-local'
    | 'file'
    | 'unit'
    | 'button' = 'text'

  /**
   * Controls whether the input expands to fill the width of its container.
   */
  @property({ reflect: true, type: Boolean }) expand = false

  /**
   * Optionally disallow certain characters from being used inside the input, using a regex pattern.
   */
  @property({ reflect: true, attribute: 'disallow-pattern' }) disallowPattern?: string = undefined

  /**
   * The inputmode attribute provides a hint to the browser about what type of keyboard to open on mobile devices.
   * Valid values: none, text, decimal, numeric, tel, search, email, url
   * When not explicitly set, defaults based on input type:
   * - type="number" → inputmode="numeric"
   * - type="email" → inputmode="email"
   * - type="tel" → inputmode="tel"
   * - type="url" → inputmode="url"
   * - type="search" → inputmode="search"
   * Can be explicitly overridden for any type.
   */
  @property({ reflect: true }) inputmode?: 'none' | 'text' | 'decimal' | 'numeric' | 'tel' | 'search' | 'email' | 'url' = undefined

  render() {
    const isNumber = this.type === 'number'

    const inputmodeValue = this.inputmode || defaultInputmodeMap[this.type]

    return html`
      ${this.renderLabel()}

      <div
        class=${classMap({
          'n-input-container': true,
          'is-rtl': this.direction.dir === 'rtl',
          'is-input-group-control': this.inputGroupControl.grouped,
          'is-input-group-size-s': this.inputGroupControl.size === 's',
          'is-input-group-size-m': this.inputGroupControl.size === 'm',
          'is-input-group-size-l': this.inputGroupControl.size === 'l',
          'has-input-group-inline-start-addon': this.inputGroupControl.hasInlineStartAddon,
          'has-input-group-inline-end-addon': this.inputGroupControl.hasInlineEndAddon,
        })}
        style=${styleMap({
          '--_n-input-start-inline-size': px(this.startObserver.inlineSize),
          '--_n-input-end-inline-size': px(this.endObserver.inlineSize),
        })}
      >
        <slot name="start">
          ${this.type === 'search' ? html`<nord-icon name="navigation-search"></nord-icon>` : nothing}
        </slot>
        <input
          ${ref(this.textSelectableRef)}
          ${ref(this.focusableRef)}
          id=${this.inputId}
          class="n-input"
          type=${isNumber || this.type === 'unit' ? 'text' : this.type}
          inputmode=${ifDefined(inputmodeValue)}
          pattern=${cond(isNumber, '[0-9]*')}
          ?disabled=${this.effectiveDisabled}
          ?required=${this.required || this.inputGroupControl.required}
          ?readonly=${this.readonly || this.inputGroupControl.readonly}
          name=${ifDefined(this.name)}
          .value=${this.type === 'file' ? '' : (this.value ?? '')}
          placeholder=${ifDefined(this.placeholder)}
          @input=${this.handleInputChange}
          @change=${this.handleChange}
          @keydown=${this.handleKeydown}
          @select=${this.handleSelect}
          aria-label=${ifDefined(this.controlAriaLabel)}
          aria-describedby=${ifDefined(this.controlAriaDescribedby)}
          aria-invalid=${ifDefined(this.controlAriaInvalid)}
          spellcheck="false"
          autocomplete=${this.autocomplete as any}
        />
        <slot name="end"></slot>
      </div>

      ${this.renderError()}
      ${this.isHintBelow ? this.renderHint() : nothing}
    `
  }

  [requestInputGroupContext]() {
    this.requestFieldContext()
    this.inputGroupControl.requestContext()
  }

  private handleKeydown(e: KeyboardEvent) {
    if (e.key !== 'Enter') {
      return
    }

    const { form } = this

    if (form) {
      const button = getSubmitButton(form)
      setTimeout(() => button?.click(), 0)
    }
  }

  private handleInputChange = (e: Event) => {
    const target = e.target as HTMLInputElement

    // clean up any invalid characters
    if (this.disallowPattern) {
      cleanValue(target, new RegExp(this.disallowPattern, 'g'))
    }
    this.handleInput(e)
  }

  protected handleSelect(e: Event) {
    e.stopPropagation()

    /**
     * Fired when some text has been selected.
     */
    this.dispatchEvent(new NordEvent('select'))
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-input': Input
  }
}
